import { cloneDeep } from "lodash";
import { AdaptedGroup, GroupEdition } from "../models";
import validateGroupEdition from "../utils/validateGroupEdition";
import { EditionsReducerType } from "./types";

export const _removeEmptyGroupEditions = (state: EditionsReducerType): EditionsReducerType => ({
  ...state,
  byGroupId: Object.fromEntries(
    Object.entries(state?.byGroupId ?? {}).filter(([, edition]) => {
      return edition == null || Object.keys(edition).length > 0;
    }),
  ),
});

export const addGroupToEdit = (
  group: AdaptedGroup,
  state: EditionsReducerType,
): EditionsReducerType => ({
  ...state,
  groupsToEditById: { ...state.groupsToEditById, [group?.id]: group },
});

export const addPendingCapacity = (
  groupId: string,
  capacity: number,
  state: EditionsReducerType,
): EditionsReducerType => ({
  ...state,
  pendingCapacitiesByGroupId: {
    ...state.pendingCapacitiesByGroupId,
    [groupId]: capacity,
  },
});

export const clean = (state: EditionsReducerType): EditionsReducerType => ({
  ...state,
  byGroupId: {},
  errorsByGroupId: {},
  groupsToEditById: {},
});

export const editGroupsToEditCapacities = (
  capacity: number,
  groupsById: Record<string, AdaptedGroup>,
  groupIdsToExclude: Set<string>,
  state: EditionsReducerType,
): EditionsReducerType => {
  const newState = cloneDeep(state);
  const groupIds = Object.keys(state?.groupsToEditById ?? {}).filter(
    groupId => !groupIdsToExclude.has(groupId),
  );

  for (const groupId of groupIds) {
    const group = groupsById[groupId];
    if (group?.capacity === capacity) {
      delete newState?.byGroupId[groupId]?.capacity;
    } else {
      newState.byGroupId[groupId] = {
        ...newState.byGroupId[groupId],
        capacity,
      };
    }
  }

  return _removeEmptyGroupEditions(newState);
};

export const editGroupsToEditVisibilities = (
  groupsById: Record<string, AdaptedGroup>,
  state: EditionsReducerType,
): EditionsReducerType => {
  const groupIds = Object.keys(state?.groupsToEditById ?? {});
  const groupVisibility = (group: AdaptedGroup) =>
    state?.byGroupId[group?.id]?.visibleForEnrollment ?? group?.visibleForEnrollment;
  const newVisibility = !groupIds
    .map(groupId => groupsById[groupId])
    .map(groupVisibility)
    .every(visibility => visibility === true);
  const newState = cloneDeep(state);

  for (const groupId of groupIds) {
    const group = groupsById[groupId];
    if (group?.visibleForEnrollment === newVisibility) {
      delete newState?.byGroupId[groupId]?.visibleForEnrollment;
    } else {
      newState.byGroupId[groupId] = {
        ...newState.byGroupId[groupId],
        visibleForEnrollment: newVisibility,
      };
    }
  }

  return _removeEmptyGroupEditions(newState);
};

export const removeGroupToEdit = (
  group: AdaptedGroup,
  state: EditionsReducerType,
): EditionsReducerType => ({
  ...state,
  groupsToEditById: Object.fromEntries(
    Object.entries(state.groupsToEditById).filter(([groupId]) => groupId !== String(group?.id)),
  ),
});

export const removePendingCapacity = (
  groupId: string,
  state: EditionsReducerType,
): EditionsReducerType => {
  const newState = cloneDeep(state);
  delete newState?.pendingCapacitiesByGroupId[groupId];
  return newState;
};

export const restoreGroupsToEditCapacities = (state: EditionsReducerType): EditionsReducerType => {
  const newState = cloneDeep(state);

  for (const groupId of Object.keys(state?.byGroupId ?? {})) {
    if (groupId in (state?.groupsToEditById ?? {})) {
      delete newState?.byGroupId[groupId]?.capacity;
    }
  }

  return _removeEmptyGroupEditions(newState);
};

export const sanitizePendingEditions = (
  subgroups: AdaptedGroup[],
  state: EditionsReducerType,
): EditionsReducerType => {
  const editableGroupIds = new Set(
    (subgroups ?? []).filter(group => group?.isEditable?.allowed).map(group => group?.id),
  );
  return {
    ...state,
    pendingCapacitiesByGroupId: Object.fromEntries(
      Object.entries(state.pendingCapacitiesByGroupId).filter(
        ([groupId]) => !editableGroupIds.has(groupId),
      ),
    ),
  };
};

export const setGroupsToEdit = (
  groupsToEditById: Record<AdaptedGroup["id"], AdaptedGroup>,
  state: EditionsReducerType,
): EditionsReducerType => ({ ...state, groupsToEditById });

export const updateErrorsByGroupId = (
  {
    group,
    editions,
    isSubgroup,
  }: { group: AdaptedGroup; editions: GroupEdition; isSubgroup: boolean },
  state: EditionsReducerType,
): EditionsReducerType => {
  const errors = validateGroupEdition(group, editions, isSubgroup);
  const errorsByGroupId = {
    ...state.errorsByGroupId,
    [group?.id]: errors,
  };

  if (Object.keys(errors).length === 0) {
    delete errorsByGroupId[group?.id];
  }

  return { ...state, errorsByGroupId };
};

export const updateGroupCapacity = (
  {
    groupId,
    value,
    originalValue,
  }: {
    groupId: AdaptedGroup["id"];
    value: string;
    originalValue: number;
  },
  state: EditionsReducerType,
): EditionsReducerType => {
  const capacity = parseInt(value);
  const newState = {
    ...state,
    byGroupId: {
      ...state.byGroupId,
      [groupId]: { ...state.byGroupId[groupId], capacity },
    },
  };

  if (capacity === originalValue) {
    delete newState.byGroupId[groupId]?.capacity;
  }

  return _removeEmptyGroupEditions(newState);
};

export const updateGroupVisibility = (
  {
    groupId,
    value,
    originalValue,
  }: {
    groupId: string;
    value: boolean;
    originalValue: boolean;
  },
  state: EditionsReducerType,
): EditionsReducerType => {
  const newState = {
    ...state,
    byGroupId: {
      ...state.byGroupId,
      [groupId]: { ...state.byGroupId[groupId], visibleForEnrollment: value },
    },
  };

  if (value === originalValue) {
    delete newState.byGroupId[groupId]?.visibleForEnrollment;
  }

  return _removeEmptyGroupEditions(newState);
};
